/**
 * Geteilte Formatter für den Schweizer Standard (AGE-162).
 *
 * Warum es diese Datei gibt: `toLocaleString()` / `toLocaleDateString()` ohne
 * Locale-Argument erbt das Locale des Browsers. Ein Makler mit en-US-Browser
 * sah dadurch `1,250,000` und `10/2/2026` statt `1'250'000` und `02.10.2026`.
 * Alle nutzersichtbaren Zahlen, Beträge und Daten laufen deshalb über diese
 * Formatter; die ESLint-Regel `no-restricted-syntax` in `eslint.config.mjs`
 * verhindert neue locale-lose Aufrufe.
 *
 * Zwei Dinge macht `de-CH` von Haus aus nicht richtig, darum die expliziten
 * Optionen unten:
 *  - `toLocaleDateString('de-CH')` liefert `2.10.2026`, unser Standard ist aber
 *    `TT.MM.JJJJ`, also `02.10.2026`.
 *  - Ohne `timeZone` hängt das Ergebnis am Gerät des Betrachters. Zeitstempel
 *    sind bei uns immer Schweizer Zeit, deshalb ist `Europe/Zurich` der Default.
 */

/** Locale für alle nutzersichtbaren Formatierungen. */
export const SWISS_LOCALE = 'de-CH';

/** Zeitzone für alle nutzersichtbaren Zeitstempel. */
export const SWISS_TIME_ZONE = 'Europe/Zurich';

/** Platzhalter, wenn kein Wert vorhanden oder ungültig ist. */
export const EMPTY_VALUE = '—';

export interface NumberFormatOptions extends Intl.NumberFormatOptions {
  /** Abweichendes Locale, z. B. für einen Export in einer anderen Sprache. */
  readonly locale?: string;
  /** Ausgabe für `null`, `undefined` und `NaN`. Default `'—'`. */
  readonly fallback?: string;
}

export interface DateFormatOptions extends Intl.DateTimeFormatOptions {
  /** Abweichendes Locale, z. B. für einen Export in einer anderen Sprache. */
  readonly locale?: string;
  /** Ausgabe für fehlende oder ungültige Daten. Default `'—'`. */
  readonly fallback?: string;
}

export type DateLike = Date | string | number | null | undefined;

/**
 * Zahl im Schweizer Format: `1250000` → `1'250'000`.
 */
export function formatNumber(
  value: number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const {
    locale = SWISS_LOCALE,
    fallback = EMPTY_VALUE,
    ...intlOptions
  } = options;

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return new Intl.NumberFormat(locale, intlOptions).format(value);
}

/**
 * Betrag in Schweizer Franken: `1250000` → `CHF 1'250'000`.
 *
 * Rappen werden standardmässig weggelassen, weil Immobilienpreise nie auf den
 * Rappen genau kommuniziert werden. Wer sie braucht, setzt
 * `minimumFractionDigits: 2` und erhält `CHF 1'250'000.00`.
 */
export function formatChf(
  value: number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const { minimumFractionDigits, maximumFractionDigits, ...rest } = options;
  // Beide Grenzen gemeinsam ableiten: würde nur `minimumFractionDigits: 2`
  // gesetzt und das Maximum bei 0 bleiben, wirft Intl eine RangeError.
  const minimum = minimumFractionDigits ?? 0;
  const maximum = maximumFractionDigits ?? Math.max(minimum, 0);

  return formatNumber(value, {
    style: 'currency',
    currency: 'CHF',
    ...rest,
    minimumFractionDigits: minimum,
    maximumFractionDigits: maximum,
  });
}

/**
 * Datum im Schweizer Format: `02.10.2026`.
 */
export function formatDate(
  value: DateLike,
  options: DateFormatOptions = {},
): string {
  return formatDateParts(value, options, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Datum mit Uhrzeit im Schweizer Format: `02.10.2026, 16:05`.
 *
 * Ohne Sekunden — die sind in der Oberfläche nie relevant und machen
 * Tabellenspalten nur breiter.
 */
export function formatDateTime(
  value: DateLike,
  options: DateFormatOptions = {},
): string {
  return formatDateParts(value, options, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Uhrzeit im Schweizer Format: `16:05`.
 */
export function formatTime(
  value: DateLike,
  options: DateFormatOptions = {},
): string {
  return formatDateParts(value, options, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateParts(
  value: DateLike,
  options: DateFormatOptions,
  defaults: Intl.DateTimeFormatOptions,
): string {
  const {
    locale = SWISS_LOCALE,
    fallback = EMPTY_VALUE,
    ...intlOptions
  } = options;
  const date = toDate(value);

  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: SWISS_TIME_ZONE,
    ...defaults,
    ...intlOptions,
  }).format(date);
}

function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // `0` ist im Code durchgehend "kein Zeitstempel" (Muster `timestamp || 0`),
  // nicht der 1. Januar 1970. Ohne diese Zeile zeigen Tabellen für fehlende
  // Daten `01.01.1970` statt des Platzhalters.
  if (value === 0) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}
